import moment from "moment-timezone";

const TIMEZONE_STORAGE_KEY = "ruttia.timezone";
let currentTimezone: string | null = null;

const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/**
 * Zona horaria de la empresa (cierres, cajas, pagos por día calendario). La envía el API al ingresar y en
 * `GET /user`; mientras no llegue se usa la última guardada en este navegador o la del navegador.
 */
export function operationalTimezone(): string {
  if (currentTimezone) return currentTimezone;
  try {
    const saved = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (saved && moment.tz.zone(saved)) {
      currentTimezone = saved;
      return saved;
    }
  } catch {
    /* sin almacenamiento: se usa la del navegador */
  }
  return browserTimezone();
}

/** Guarda la zona horaria de la empresa que devolvió el API (se ignora si no es válida). */
export function setOperationalTimezone(timezone: unknown): void {
  if (typeof timezone !== "string" || !moment.tz.zone(timezone)) return;
  currentTimezone = timezone;
  try {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
  } catch {
    /* sin almacenamiento: queda solo en memoria */
  }
}

/** `moment` anclado a la zona operativa (preferir esto a `moment()` sin tz). */
export function operationalMoment(): moment.Moment {
  return moment.tz(operationalTimezone());
}

/** Fecha calendario hoy en zona operativa (YYYY-MM-DD). */
export function nowOperationalDateString(): string {
  return operationalMoment().format("YYYY-MM-DD");
}

/** Fecha y hora actual en zona operativa para persistir (YYYY-MM-DD HH:mm:ss). */
export function nowOperationalDateTimeString(): string {
  return operationalMoment().format("YYYY-MM-DD HH:mm:ss");
}

/** El menor de dos fechas calendario YYYY-MM-DD (comparación lexicográfica). */
export function minYmd(a: string, b: string): string {
  if (!a) return b;
  if (!b) return a;
  return a <= b ? a : b;
}

/**
 * Interpreta valores de fecha/hora del backend o de inputs y devuelve solo la fecha calendario en zona operativa.
 * Evita `new Date(x).toISOString().split('T')[0]` (UTC).
 */
export function formatOperationalDateOnly(raw: string | Date | null | undefined): string {
  if (raw == null || raw === "") {
    return "";
  }
  if (raw instanceof Date) {
    return moment(raw).tz(operationalTimezone()).format("YYYY-MM-DD");
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && (s.endsWith("Z") || /[+-]\d{2}/.test(s))) {
    return moment.utc(s).tz(operationalTimezone()).format("YYYY-MM-DD");
  }
  const m = moment.tz(s, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD"], operationalTimezone());
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
}
